/**
 * Vehicle type dropdown options, derived from `GET /v3/vehicleTypes`.
 *
 * Vehicle types used to be a hardcoded array here. They are now admin-managed reference data in
 * Horizon, so the list is fetched (see `useVehicleTypeOptions`) and this module only shapes it.
 */

/** Fallback list used only when the endpoint is unreachable, so the form still renders something. */
export const FALLBACK_VEHICLE_TYPES = [
    'Light Duty Truck',
    'Medium Duty Truck',
    'Heavy Duty Truck',
    'Commercial Bus',
    'SUV',
];

export const FALLBACK_VEHICLE_TYPE_OPTIONS = FALLBACK_VEHICLE_TYPES.map((type) => ({
    value: type,
    label: type,
}));

/**
 * Sentinel option value for the "+ Add new vehicle type" row. Selecting it opens an input below the
 * select rather than changing the preset's type — same shape as EvTariffModal's 'CUSTOM' preset.
 *
 * Type names are free text, so a name COULD equal a sentinel; `buildVehicleTypeOptions` filters those
 * rows out rather than letting one shadow an action row.
 */
export const ADD_VEHICLE_TYPE_VALUE = '__ADD_VEHICLE_TYPE__';

/** Sentinel for the "Manage vehicle types" row, rendered below the add row. Opens the manage dialog. */
export const MANAGE_VEHICLE_TYPES_VALUE = '__MANAGE_VEHICLE_TYPES__';

/** Unwrap the axios/express envelope: { data: { data: [rows] } }. */
export const extractVehicleTypeData = (response) => {
    if (!response) return [];
    if (Array.isArray(response)) return response;
    const payload = response.data ?? response;
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.data)) return payload.data;
    if (Array.isArray(payload?.vehicleTypes)) return payload.vehicleTypes;
    return [];
};

/**
 * `{ value, label }` shape consumed by the shared `DropDownList`. `value === label` because the
 * literal name string is what gets persisted on the preset.
 *
 * The API also serves each type's tonnage `description`, deliberately NOT mapped here: this dropdown
 * shows names only. (Passing it through as `subtitle` would make `DropDownList`'s `getOptionLabel`
 * render it beside the name.) The VelocityAI console is what displays that descriptor.
 */
export const buildVehicleTypeOptions = (apiRows) => {
    if (!Array.isArray(apiRows) || apiRows.length === 0) return [];
    return apiRows
        .map((row) => {
            const name = String(row?.name || '').trim();
            if (!name) return null;
            // A type named exactly like a sentinel would be unselectable and would shadow an action
            // row, so drop it rather than render it.
            if (name === ADD_VEHICLE_TYPE_VALUE || name === MANAGE_VEHICLE_TYPES_VALUE) return null;
            return { value: name, label: name };
        })
        .filter(Boolean);
};

/**
 * Keep a saved preset value visible even when it is not in the fetched list — the type may have been
 * deactivated since, or predate the collection. Mirrors `mergeFuelTariffOptions`.
 *
 * This is what replaced the old `isValidVehicleType(x) ? x : ''` hydration guards: blanking an
 * unrecognised value silently discarded a valid saved classification, and once the list became async
 * it would also have fired during the load window on every mount.
 */
export const mergeVehicleTypeOptions = (options, savedValue) => {
    const trimmed = String(savedValue || '').trim();
    if (!trimmed) return options;
    if (options.some((option) => option.value === trimmed)) return options;
    // Prepended, matching mergeFuelTariffOptions: appending would sit the orphaned value at the
    // bottom of the menu next to the admin action rows, reading as though it were one of them.
    return [{ value: trimmed, label: trimmed }, ...options];
};
